import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cohortApi } from '../api/cohorts';
import { Link } from 'react-router-dom';

interface Cohort {
  id: string;
  name: string;
  canvasCourseId: string;
  standardReference: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  standard?: {
    title: string;
    level: number;
  };
}

export const CoursesList: React.FC = () => {
  const { user } = useAuth();
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCohorts = useCallback(async () => {
    if (!user?.tenant_id) return;

    try {
      setLoading(true);
      const response = await cohortApi.getAll(user.tenant_id);
      setCohorts(response.data);
    } catch (err) {
      console.error('Failed to load courses:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    loadCohorts();
  }, [loadCohorts]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const canvasUrl = 'https://graddings2.instructure.com';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading courses...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Canvas Courses</h1>
        <p className="text-gray-600">
          {cohorts.length} apprenticeship courses linked to Canvas
        </p>
      </div>

      {cohorts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">No Canvas courses created yet.</p>
          <Link to="/admin" className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition">
            Create First Course
          </Link>
        </div>
      ) : (
        <div className="grid gap-6">
          {cohorts.map((cohort) => (
            <div key={cohort.id} className="bg-white rounded-lg shadow hover:shadow-md transition">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{cohort.name}</h3>
                    {cohort.standard && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Standard:</span> {cohort.standard.title} (Level {cohort.standard.level})
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Reference:</span> {cohort.standardReference}
                    </p>
                  </div>
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${cohort.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {cohort.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-md p-3">
                    <div className="text-xs text-gray-500 mb-1">Canvas Course ID</div>
                    <div className="font-mono font-semibold text-gray-900">{cohort.canvasCourseId}</div>
                  </div>
                  <div className="bg-gray-50 rounded-md p-3">
                    <div className="text-xs text-gray-500 mb-1">Start Date</div>
                    <div className="font-medium text-gray-900">{formatDate(cohort.startDate)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-md p-3">
                    <div className="text-xs text-gray-500 mb-1">End Date</div>
                    <div className="font-medium text-gray-900">{formatDate(cohort.endDate)}</div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <a href={canvasUrl + '/courses/' + cohort.canvasCourseId} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition">
                    Open in Canvas
                  </a>
                  <a href={canvasUrl + '/courses/' + cohort.canvasCourseId + '/settings'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition">
                    Settings
                  </a>
                  <a href={canvasUrl + '/courses/' + cohort.canvasCourseId + '/users'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition">
                    People
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
